import { TrendingUp, Globe2, Smartphone, Fingerprint, Bot, Zap } from "lucide-react";
import { GeometricBadge } from "../playful/GeometricBadge";
import { Reveal, useInView, reveal } from "./Reveal";
import { AnalyticsMock } from "./mocks";

const POINTS = [
  { icon: TrendingUp, text: "14-day click trend on every link" },
  { icon: Globe2, text: "Country-level geographic breakdown" },
  { icon: Smartphone, text: "Device, browser & referrer analytics" },
  { icon: Fingerprint, text: "Unique visitor deduplication" },
  { icon: Bot, text: "Automatic bot traffic filtering" },
  { icon: Zap, text: "Live click feed + AI weekly summaries" },
];

/** AnalyticsSection — decisions over dashboards. Dark band for contrast. */
export default function AnalyticsSection() {
  const [ref, visible] = useInView(0.08);
  return (
    <section
      className="overflow-hidden border-y-2 border-ink bg-ink text-white"
      data-testid="analytics-section"
    >
      <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10">
        <div ref={ref} className="flex flex-col items-center gap-10 lg:flex-row">
          {/* Copy */}
          <div className="w-full min-w-0 lg:w-[42%]">
            <div {...reveal(visible, 0)}>
              <GeometricBadge tone="mint">ANALYTICS &amp; INTELLIGENCE</GeometricBadge>
              <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.92] tracking-tight md:text-6xl">
                Attribution,
                <span className="ai-gradient-text"> not guesswork.</span>
              </h2>
              <p className="mt-5 max-w-md font-sans text-base leading-relaxed text-white/70 md:text-lg">
                The reporting layer marketers and agencies actually trust. Every click is a signal —
                who clicked, from where, on what device, and what it drove.
              </p>
              {/* Headline metrics */}
              <div className="mt-7 flex flex-wrap gap-3">
                {[
                  { v: "Real-time", l: "click feed" },
                  { v: "7×24", l: "heatmap" },
                  { v: "Bot-filtered", l: "by default" },
                ].map((m) => (
                  <div
                    key={m.l}
                    className="rounded-2xl border-2 border-white/20 bg-white/5 px-4 py-2.5"
                  >
                    <div className="font-display text-lg font-extrabold leading-none text-mint">
                      {m.v}
                    </div>
                    <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-white/50">
                      {m.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-3">
              {POINTS.map(({ icon: Icon, text }, i) => (
                <div
                  key={text}
                  className="flex items-center gap-3"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? "none" : "translateX(-16px)",
                    transition: "opacity 0.5s ease, transform 0.5s ease",
                    transitionDelay: `${200 + i * 60}ms`,
                  }}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10">
                    <Icon size={15} strokeWidth={2} className="text-mint" />
                  </div>
                  <span className="font-sans text-sm text-white/80">{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Analytics mock */}
          <div className="w-full min-w-0 lg:w-[58%]" {...reveal(visible, 120)}>
            <AnalyticsMock animate={visible} />
          </div>
        </div>
      </div>
    </section>
  );
}
